import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAlerts } from "src/hooks/useAlerts";
import { getGenderId } from "src/modules/page";
import { useSimpleDiscountTimerBar } from "./useDiscountTimerBar";
import { AlertBar } from "src/types/graphql.d";
import { getStorageJSON, setStorageJSON } from "src/utils/helper/localStorage";
import { compareAlerts } from "src/utils/helper/alerts";

type RibbonState = {
  showRibbon: boolean;
  toggleShowRibbon: () => void;
  combinedAlertBars: AlertBar[];
  discountMessage: AlertBar | undefined;
};

export const RIBBON_STORAGE_KEY = "ot_alertbar_status";

function useRibbon(): RibbonState {
  const initialLoad = useRef(true);
  const [showRibbon, setShowRibbon] = useState(false);
  const genderId = useSelector(getGenderId) || "women";
  const { data, loading: alertLoading } = useAlerts(genderId);
  const { data: discountTimer, loading: discountLoading } =
    useSimpleDiscountTimerBar({
      shop_type: genderId,
    });

  const isLoading = useMemo(
    () => alertLoading || discountLoading,
    [alertLoading, discountLoading]
  );

  const { combinedAlertBars, alertsCache } = useMemo(() => {
    if (alertLoading || discountLoading || initialLoad.current) {
      return {
        combinedAlertBars: [],
        alertsCache: [],
        shouldShowRibbon: false,
      };
    }

    const alertBars = data?.alertBar?.alert;

    const combinedAlerts = [
      ...(alertBars || []),
      ...(discountTimer?.discountTimerBar?.message
        ? [discountTimer?.discountTimerBar?.message]
        : []),
    ];

    const alertsCache = combinedAlerts?.flatMap<string>((item) =>
      btoa(JSON.stringify(item))
    );

    return { combinedAlertBars: combinedAlerts, alertsCache };
  }, [
    alertLoading,
    discountLoading,
    data?.alertBar?.alert,
    discountTimer?.discountTimerBar?.message,
  ]);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (isLoading) {
      return;
    }

    const hasNewAlerts = compareAlerts({
      gender: genderId,
      alerts: alertsCache,
    });

    setShowRibbon(!hasNewAlerts);
  }, [genderId, alertsCache, isLoading]);

  const toggleShowRibbon = useCallback(() => {
    const storedAlerts =
      getStorageJSON<Record<string, string[]>>(RIBBON_STORAGE_KEY) || {};

    setStorageJSON<Record<string, string[]>>(RIBBON_STORAGE_KEY, {
      ...storedAlerts,
      [genderId]: alertsCache,
    });

    setShowRibbon(false);
  }, [alertsCache, genderId]);

  return {
    showRibbon,
    toggleShowRibbon,
    combinedAlertBars,
    discountMessage: discountTimer?.discountTimerBar?.message as AlertBar,
  };
}

export { useRibbon };
